"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import { Mic, Brain, Heart, Moon, Crown, Sparkles, ArrowRight, Lightbulb } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";
import { Badge } from "@/components/ui/badge";

const TOPICS = [
  { icon: Mic, title: "How Excellence Actually Runs", abstract: "Why execution beats vision, and what an operating system for excellence actually contains. The thesis presentation — organizations that treat excellence as a value fail; organizations that engineer it as a system succeed.", audience: "Leadership Summits", href: "/speaking/how-excellence-actually-runs/", gradient: "from-red/15", accent: "text-red" },
  { icon: Brain, title: "Leadership as Diagnosis", abstract: "What medicine teaches about running organizations. Diagnose before treating. Triage under pressure. Run the protocol while keeping judgment. Own the outcome. Do no harm.", audience: "Medical & Executive", href: "/speaking/leadership-as-diagnosis/", gradient: "from-purple/15", accent: "text-purple" },
  { icon: Heart, title: "The 70% Project", abstract: "The operating case for the talent the world overlooks. Why teams built 70% women outperform — not as a moral argument, but as an operating one, backed by a decade of evidence.", audience: "Diversity & Inclusion events", href: "/speaking/the-70-percent-project/", gradient: "from-amber-gold/15", accent: "text-amber-gold" },
  { icon: Moon, title: "Build in the Dark", abstract: "The operator's discipline in an attention economy. Why publicity is not impact, and why the things that endure are built quietly, measured by outcomes rather than visibility.", audience: "Founder Forums", href: "/speaking/build-in-the-dark/", gradient: "from-denim/15", accent: "text-denim" },
  { icon: Sparkles, title: "The Triple Threat", abstract: "Medicine, technology, and politics as one integrated operating method. How clinical rigor, applied to technological systems under political-grade stakes, produces outcomes no single-domain specialist can achieve.", audience: "Cross-domain Conferences", href: "/speaking/the-triple-threat/", gradient: "from-lime/15", accent: "text-lime" },
  { icon: Crown, title: "Women in the C-Suite", abstract: "Lessons from a decade of operating at sovereign scale as a woman — building teams, breaking barriers, and proving that the best operators are the ones the world overlooks.", audience: "Women's leadership summits", href: "/speaking/women-breaking-barriers/", gradient: "from-amber-gold/15", accent: "text-amber-gold" },
];

export function SpeakingTopicsSection() {
  return (
    <Section id="speaking-topics" ariaLabel="Speaking Topics" background="default">
      <SectionHeader
        icon={Mic}
        overline="Speaking Topics"
        overlineClassName="text-red"
        pillClassName="glass-tinted-red border-red/20"
        title={
          <>
            Signature Keynotes and <span className="text-gradient-energy">Engagement Focus</span>
          </>
        }
        subtitle="Speaking and advisory work grounded in fifteen-plus years of sovereign-scale operations. Every topic backed by verified data, not theory."
      />

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5">
        {TOPICS.map((t, i) => (
          <motion.div
            key={t.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            transition={{ duration: 0.5, delay: i * 0.06 }}
          >
            <Link href={t.href} className="block h-full group">
              <div className={`relative glass-card rounded-2xl p-6 h-full overflow-hidden bg-gradient-to-br ${t.gradient} to-transparent`}>
                <div aria-hidden className="absolute -top-16 -right-16 size-48 rounded-full bg-current opacity-[0.04] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className="flex items-start justify-between mb-4">
                    <div className="size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      <t.icon className="size-6 text-white" />
                    </div>
                    <Badge variant="outline" className="text-caption font-normal">{t.audience}</Badge>
                  </div>
                  <h3 className={`text-heading-md mb-3 ${t.accent}`}>{t.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{t.abstract}</p>
                  <div className={`inline-flex items-center gap-1.5 text-body-sm font-semibold ${t.accent} group-hover:gap-2.5 transition-all`}>
                    Explore topic <ArrowRight className="size-4" />
                  </div>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}
